import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';

import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button, Divider, MenuItem, TextField, useTheme } from '@mui/material';

import { ClientResponseDto } from '@bandido/shared/dtos';
import { DUPLICATED_EMAIL_ERROR, DUPLICATED_PHONE_ERROR, DUPLICATED_WHATSAPP_ERROR, MISSING_FIELDS_ERROR } from '@bandido/shared/constants';

import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { ControlledSelect } from '@frontend/features/common/components/ControlledSelect';
import { DocumentType } from '@frontend/features/auth/types';
import { countries } from '@frontend/features/common/constants/countries';
import { CountryFlag } from '@frontend/features/common/components/CountryFlag';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

interface FormProps {
  onClose: () => void;
  data?: ClientResponseDto;
}

interface CreateClientDTO {
  firstName: string;
  lastName: string;
  agency: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
  email?: string;
  address1?: string;
  address2?: string;
  country?: string;
  city?: string;
  postalCode?: string;
  documentType?: string;
  documentNumber?: string;
  whatsappNumber?: string;
}

export const Form = ({ onClose, data }: FormProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();

  const resolver = yupResolver(
    yup.object().shape({
      firstName: yup.string().required(),
      lastName: yup.string().required(),
      agency: yup.string().required(),
      email: yup.string().email().optional(),
      whatsappNumber: yup.string().optional(),
      phoneCountryCode: yup
        .string()
        .optional(),
      phoneNumber: yup.string().optional(),
      address1: yup.string().optional(),
      address2: yup.string().optional(),
      country: yup
        .string()
        .optional(),
      city: yup.string().optional(),
      postalCode: yup.string().optional(),
      documentType: yup.mixed<DocumentType>().oneOf(Object.values(DocumentType)).optional(),
      documentNumber: yup.string().optional(),
      form: yup.string().optional().test(
        'email-or-phone',
        function() {
          const { email, phoneNumber, phoneCountryCode, whatsappNumber } = this.parent;
          const hasEmail = Boolean(email);
          const hasWhatsapp = Boolean(whatsappNumber);
          const hasPhone = Boolean(phoneCountryCode) && Boolean(phoneNumber);
  
          if (hasEmail || hasPhone || hasWhatsapp) {
            return true;
          } else {
            return this.createError({
              path: 'form',
              message: t('clients.create.createdErrorMissingFields')
            });
          }
        }
      )
    })
  );

  const methods = useForm({
    resolver,
  });
  const { handleSubmit, formState: { errors } } = methods;

  const onSubmit = async (formData: CreateClientDTO) => {
    try {
      await client.put(`/client/${data?.id}`, formData);
      addToast({
        message: t('clients.edit.createdSuccess'),
        color: 'success',
      });
      queryClient.invalidateQueries();
      onClose();
    } catch (e: any) {
      if (e.response?.data?.error === DUPLICATED_EMAIL_ERROR) {
        addToast({
          message: t('clients.create.createdErrorEmail'),
          color: 'error',
        });

      } else if (e.response?.data?.error === DUPLICATED_PHONE_ERROR) {
        addToast({
          message: t('clients.create.createdErrorPhone'),
          color: 'error',
        });
      } else if (e.response?.data?.error === MISSING_FIELDS_ERROR) {
        addToast({
          message: t('clients.create.createdErrorMissingFields'),
          color: 'error',
        });
      } else if (e.response?.data?.error === DUPLICATED_WHATSAPP_ERROR) {
        addToast({
          message: t('clients.create.createdErrorWhatsapp'),
          color: 'error',
        });
      } else {
        addToast({
          message: t('clients.create.createdError'),
          color: 'error',
        });
      }
    }
  };

  useEffect(() => {
    if (data) {
      methods.setValue('address1', data.address1 || '', { shouldDirty: true });
      methods.setValue('address2', data.address2 || '', { shouldDirty: true });
      // @ts-ignore FIXME:
      methods.setValue('agency', data.agencyId || '', { shouldDirty: true });
      methods.setValue('city', data.city || '', { shouldDirty: true });
      data.country && methods.setValue('country', data.country, { shouldDirty: true });
      methods.setValue('documentNumber', data.documentNumber || '', { shouldDirty: true });
      methods.setValue('email', data.email || '', { shouldDirty: true });
      methods.setValue('firstName', data.firstName || '', { shouldDirty: true });
      data.documentType && methods.setValue('documentType', data.documentType as DocumentType, { shouldDirty: true });
      methods.setValue('lastName', data.lastName || '', { shouldDirty: true });
      data.phoneCountryCode && methods.setValue('phoneCountryCode', data.phoneCountryCode, { shouldDirty: true });
      methods.setValue('phoneNumber', data.phoneNumber || '', { shouldDirty: true });
      methods.setValue('postalCode', data.postalCode || '', { shouldDirty: true });
      methods.setValue('whatsappNumber', data.whatsappNumber || '', { shouldDirty: true });
    }
  }, [data, methods]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gap={2}
          sx={{
            maxHeight: 'calc(100vh - 300px)',
            overflowX: 'auto',
            paddingTop: theme.spacing(1),
          }}
        >
          {errors?.form?.message && (
            <Box gridColumn="span 12" gap={2}>
              <Alert severity="error">
                {errors.form.message}
              </Alert>
            </Box>
          )}

          <Box gridColumn="span 12">
            <ControlledTextField
              required
              control={methods.control}
              name="agency"
              sx={{ display: 'none' }}
            />
            <TextField
              fullWidth
              disabled
              size='small'
              value={data?.agencyName}
              label={t('clients.edit.agency')}
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="firstName"
              label={t('clients.edit.firstName')}
              placeholder={t('common.form.onlyLetters')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="lastName"
              label={t('clients.edit.lastName')}
              placeholder={t('common.form.onlyLetters')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="email"
              label={t('clients.edit.email')}
              placeholder={t('common.form.onlyLetters')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledSelect
              variant="outlined"
              name="country"
              control={methods.control}
              placeholder={t('common.form.select')}
              label={t('clients.edit.country')}
              size="small"
              fullWidth
            >
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  <CountryFlag countryCode={country.code} />
                  {country.name}
                </MenuItem>
              ))}
            </ControlledSelect>
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="city"
              label={t('clients.edit.city')}
              placeholder={t('common.form.onlyLetters')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="postalCode"
              label={t('clients.edit.postalCode')}
              placeholder={t('common.form.onlyLetters')}
              fullWidth
            />
          </Box>

          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="address1"
              label={t('clients.edit.address1')}
              placeholder={t('common.form.onlyLetters')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="address2"
              label={t('clients.edit.address2')}
              placeholder={t('common.form.onlyLetters')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 5">
            <ControlledSelect
              variant="outlined"
              name="phoneCountryCode"
              control={methods.control}
              placeholder={t('common.form.select')}
              label={t('clients.edit.phoneCountryCode')}
              size="small"
              fullWidth
            >
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.phoneCode}>
                  <CountryFlag countryCode={country.code} />
                  {country.code} (+{country.phoneCode})
                </MenuItem>
              ))}
            </ControlledSelect>
          </Box>
          <Box gridColumn="span 7">
            <ControlledTextField
              control={methods.control}
              name="phoneNumber"
              label={t('clients.edit.phoneNumber')}
              placeholder={t('common.form.onlyNumbers')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="whatsappNumber"
              label={t('clients.edit.whatsappNumber')}
              placeholder={t('common.form.onlyNumbers')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 4">
            <ControlledSelect
              variant="outlined"
              name="documentType"
              control={methods.control}
              placeholder={t('common.form.select')}
              label={t('clients.edit.documentType')}
              size="small"
              fullWidth
              defaultValue={DocumentType.CI}
            >
              <MenuItem value={DocumentType.CI}>{DocumentType.CI}</MenuItem>
              <MenuItem value={DocumentType.DNI}>{DocumentType.DNI}</MenuItem>
              <MenuItem value={DocumentType.PASSPORT}>{DocumentType.PASSPORT}</MenuItem>
            </ControlledSelect>
          </Box>
          <Box gridColumn="span 8">
            <ControlledTextField
              control={methods.control}
              name="documentNumber"
              label={t('clients.edit.documentNumber')}
              placeholder={t('common.form.onlyNumbers')}
              fullWidth
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" gap={theme.spacing(3)} paddingTop={theme.spacing(3)}>
          <Divider />
          <Box
            display="flex"
            justifyContent="space-evenly"
            width="100%"
            gap={theme.spacing(1)}
            sx={{
              '& > button': {
                width: 'calc(50% - 4px)',
              },
            }}
          >
            <Button variant="outlined" color="error" size="medium" onClick={onClose}>
              {t('common.form.discard')}
            </Button>
            <Button variant="contained" color="primary" type="submit" size="medium">
              {t('common.form.save')}
            </Button>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};
